import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import Button from 'components/Button';
import { SubSectionWithHeader as SubSection } from 'components/Layout/SubSection';
import { useAsync } from 'react-use';
import ActivityService from 'services/ActivityService';
import Automation2Service from 'services/Automation2Service';
import ConfigurationService from 'services/ConfigurationService';
import CustomObjectsService from 'services/CustomObjectsService';
import {
  fetchAllDashboardsQuery,
  fetchDashboardsQuery,
} from 'components/AccessRequests/useDashboardList';
import FormService from 'services/FormService';
import LibraryService from 'services/LibraryService';
import Loader from 'components/Kizen/Loader';
import SmartConnectorService from 'services/SmartConnectorService';
import SurveyService from 'services/SurveyService';
import TeamMemberService from 'services/TeamMemberService';
import ToolbarService from 'services/ToolbarService';
import { EMPTY_ARRAY } from 'utility/fieldHelpers';
import { useEffect, useMemo, useState } from 'react';
import { useKeyListeners } from 'hooks/keyboardEventHandler/useKeyListeners';
import { KeyBoardContext } from 'hooks/keyboardEventHandler/useKeyBoardContext';
import { toastVariant, useToast } from 'components/ToastProvider';
import { useSelector } from 'react-redux';
import {
  getBusinessTeamMembersObject,
  getChosenBusiness,
} from 'store/authentication/selectors';
import { FORCE_ALL_RECORDS_SIZE, snakeToCamelCaseKeys } from 'services/helpers';
import { getErrorMessage } from 'hooks/useErrors';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import { setError } from 'store/errors';
import { getAutomationFolders } from '@kizen/api/automation';
import { buildAutomationTree } from './components/AutomationExport/helpers';
import { AutomationsExportGroup } from './components/AutomationExport/AutomationsExportGroup';
import { ExportGroup } from './components/ExportGroup';
import { ExportConfigurationModal } from './components/ExportConfigurationModal';

export const ExportSection = ({ setDirty }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showToast] = useToast();
  const { admin_permission_role, business_variables } =
    useSelector(getChosenBusiness);
  const teamMembersObject = useSelector(getBusinessTeamMembersObject);

  const [selectedObjects, setSelectedObjects] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [selectedFilterGroups, setSelectedFilterGroups] = useState([]);
  const [selectedHomepages, setSelectedHomepages] = useState([]);
  const [selectedDashboards, setSelectedDashboards] = useState([]);
  const [selectedChartGroups, setSelectedChartGroups] = useState([]);
  const [selectedToolbarTemplates, setSelectedToolbarTemplates] = useState([]);
  const [selectedEmailTemplates, setSelectedEmailTemplates] = useState([]);
  const [selectedAutomations, setSelectedAutomations] = useState([]);
  const [selectedAutomationFolders, setSelectedAutomationFolders] = useState(
    []
  );
  const [selectedForms, setSelectedForms] = useState([]);
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [selectedSmartConnectors, setSelectedSmartConnectors] = useState([]);
  const [selectedBusinessVariables, setSelectedBusinessVariables] = useState(
    []
  );
  const [selectedTeamMembersFields, setSelectedTeamMembersFields] = useState(
    []
  );
  const [selectedIntegrationSecrets, setSelectedIntegrationSecrets] = useState(
    []
  );
  const [submitting, setSubmitting] = useState(false);
  const [showFilenameModal, setShowFilenameModal] = useState(false);

  const isDirty =
    selectedObjects.length > 0 ||
    selectedRoles.length > 0 ||
    selectedActivities.length > 0 ||
    selectedFilterGroups.length > 0 ||
    selectedHomepages.length > 0 ||
    selectedDashboards.length > 0 ||
    selectedChartGroups.length > 0 ||
    selectedToolbarTemplates.length > 0 ||
    selectedEmailTemplates.length > 0 ||
    selectedAutomations.length > 0 ||
    selectedAutomationFolders.length > 0 ||
    selectedForms.length > 0 ||
    selectedSurveys.length > 0 ||
    selectedSmartConnectors.length > 0 ||
    selectedBusinessVariables.length > 0 ||
    selectedTeamMembersFields.length > 0 ||
    selectedIntegrationSecrets.length > 0;

  useEffect(() => {
    setDirty(isDirty);
  }, [setDirty, isDirty]);

  const {
    value: [
      customObjects = EMPTY_ARRAY,
      roles = EMPTY_ARRAY,
      activities = EMPTY_ARRAY,
      toolbarTemplates = EMPTY_ARRAY,
      emailTemplates = EMPTY_ARRAY,
      automations = EMPTY_ARRAY,
      homepages = EMPTY_ARRAY,
      dashboards = EMPTY_ARRAY,
      forms = EMPTY_ARRAY,
      surveys = EMPTY_ARRAY,
      smartConnectors = EMPTY_ARRAY,
      teamMembersFields = EMPTY_ARRAY,
      integrationSecrets = EMPTY_ARRAY,
      automationFolders = EMPTY_ARRAY,
    ] = EMPTY_ARRAY,
    loading: loadingCustomObjects,
  } = useAsync(async () => {
    try {
      const [
        objects,
        roles,
        activities,
        toolbarTemplates,
        emailTemplates,
        automations,
        homepages,
        dashboards,
        forms,
        surveys,
        smartConnectors,
        teamMembersFields,
        { results: integrationSecrets },
        automationFolders,
      ] = await Promise.all([
        CustomObjectsService.getCustomObjectListLight(),
        TeamMemberService.getRolesOptionsList(),
        ActivityService.v2GetActivityFullList({
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'name',
        }),
        ToolbarService.getTemplateFullList({
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'name',
          light: true,
        }),
        LibraryService.getTemplateFullList({
          page: { size: FORCE_ALL_RECORDS_SIZE },
          ordering: 'name',
        }),
        Automation2Service.getAutomationFullList({
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'name',
        }),
        fetchDashboardsQuery('', false, undefined, 'homepage'),
        fetchAllDashboardsQuery(),
        FormService.getFullList({
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'related_object__object_name,name',
        }),
        SurveyService.getFullList({
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'related_object__object_name,name',
        }),
        SmartConnectorService.getSmartConnectorFullList({
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'name',
        }),
        CustomObjectsService.getObjectFieldsWithoutRestrictions(
          teamMembersObject.id
        ),
        TeamMemberService.getIntegrationSecrets({
          page: 1,
          page_size: FORCE_ALL_RECORDS_SIZE,
          ordering: 'description',
        }),
        getAutomationFolders(AxiosService),
      ]);

      return [
        objects.map(({ id, name, objectName }) => ({
          value: id,
          label: objectName,
          data: { id, name, include_related_objects: false },
        })),
        roles
          .filter(({ value }) => value !== admin_permission_role.id)
          .map(({ value, label }) => ({
            value,
            label,
            data: { id: value, name: label },
          })),
        activities.map(({ id, name }) => ({
          value: id,
          label: name,
          data: { id, name },
        })),
        toolbarTemplates.map(({ id, name }) => ({
          value: id,
          label: name,
          data: { id, name },
        })),
        emailTemplates.map(({ id, name }) => ({
          value: id,
          label: name,
          data: { id, name },
        })),
        automations.map(({ id, apiName, name, folder }) => ({
          value: id,
          label: name,
          data: { id, name: apiName },
          folderId: folder?.id,
        })),
        homepages
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ id, name, api_name }) => ({
            value: id,
            label: name,
            data: { id, name: api_name },
          })),
        dashboards
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({ id, name, api_name }) => ({
            value: id,
            label: `${name} - (${api_name})`,
            data: { id, name: api_name },
          })),
        forms.map(({ id, name, relatedObject }) => ({
          value: id,
          label: `${relatedObject.objectName} - ${name}`,
          data: { id, name },
        })),
        surveys.map(({ id, name, relatedObject }) => ({
          value: id,
          label: `${relatedObject.objectName} - ${name}`,
          data: { id, name },
        })),
        smartConnectors.map(({ id, name, api_name }) => ({
          value: id,
          label: name,
          data: { id, name: api_name },
        })),
        teamMembersFields
          .sort(({ order: a }, { order: b }) => a - b)
          .map(({ id, name, displayName }) => ({
            value: id,
            label: displayName,
            data: { id, name },
          })),
        integrationSecrets.map(({ id, description, api_name }) => ({
          value: id,
          label: description,
          data: { id, name: api_name },
        })),
        automationFolders,
      ];
    } catch (error) {
      dispatch(
        setError({
          text: error.toString(),
          code: error.response.status,
          url: error.config.url,
        })
      );
    }
  }, [dispatch, teamMembersObject.id]);

  const businessVariables = useMemo(
    () =>
      business_variables.map(({ id, name, api_name }) => ({
        value: id,
        label: name,
        data: { id, api_name },
      })),
    [business_variables]
  );

  const { rootAutomations, folderTree } = useMemo(
    () => buildAutomationTree(automationFolders, automations),
    [automationFolders, automations]
  );

  const { assignFieldHandle, getKeyListenersProps } = useKeyListeners(
    [
      { id: 'customObjects' },
      { id: 'roles' },
      { id: 'activities' },
      { id: 'filterGroups' },
      { id: 'toolbarTemplates' },
      { id: 'emailTemplates' },
      { id: 'automations' },
      { id: 'homepages' },
      { id: 'dashboards' },
      { id: 'chartGroups' },
      { id: 'forms' },
      { id: 'surveys' },
      { id: 'smartConnectors' },
      { id: 'businessVariables' },
      { id: 'teamMembersFields' },
      { id: 'integrationSecrets' },
    ],
    { suppressAutoScroll: true }
  );

  const handleSubmit = async (prefix) => {
    const data = {
      custom_objects: selectedObjects.map(({ data }) => data),
      roles: selectedRoles.map(({ data }) => data),
      activity_objects: selectedActivities.map(({ data }) => data),
      custom_object_filter_groups: selectedFilterGroups.map(({ data }) => data),
      toolbar_templates: selectedToolbarTemplates.map(({ data }) => data),
      email_templates: selectedEmailTemplates.map(({ data }) => data),
      automations: selectedAutomations.map(({ data }) => data),
      automations_folders: selectedAutomationFolders.map((id) => ({ id })),
      homepages: selectedHomepages.map(({ data }) => data),
      dashboards: selectedDashboards.map(({ data }) => data),
      chart_groups: selectedChartGroups.map(({ data }) => data),
      forms: selectedForms.map(({ data }) => data),
      surveys: selectedSurveys.map(({ data }) => data),
      smart_connectors: selectedSmartConnectors.map(({ data }) => data),
      business_variables: selectedBusinessVariables.map(({ data }) => data),
      employee_fields: selectedTeamMembersFields.map(({ data }) => data),
      integration_secrets: selectedIntegrationSecrets.map(({ data }) => data),
      ...(prefix.trim() ? { filename_prefix: prefix.trim() } : {}),
    };

    setSubmitting(true);

    try {
      await ConfigurationService.exportConfiguration(data);

      setSelectedObjects([]);
      setSelectedRoles([]);
      setSelectedActivities([]);
      setSelectedFilterGroups([]);
      setSelectedToolbarTemplates([]);
      setSelectedEmailTemplates([]);
      setSelectedAutomations([]);
      setSelectedAutomationFolders([]);
      setSelectedHomepages([]);
      setSelectedDashboards([]);
      setSelectedChartGroups([]);
      setSelectedForms([]);
      setSelectedSurveys([]);
      setSelectedSmartConnectors([]);
      setSelectedBusinessVariables([]);
      setSelectedTeamMembersFields([]);
      setSelectedIntegrationSecrets([]);
      setShowFilenameModal(false);

      showToast({
        message: t('Export queued. Check your email for download link.'),
        variant: toastVariant.SUCCESS,
      });
    } catch (err) {
      showToast({
        message:
          getErrorMessage(getOriginalError(err)) ||
          t('Export failed. Please try again or contact Kizen support.'),
        variant: toastVariant.FAILURE,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const checkboxesConfig = [
    {
      name: 'customObjects',
      label: t('Custom Objects'),
      options: customObjects,
      selected: selectedObjects,
      setSelected: setSelectedObjects,
    },
    {
      name: 'roles',
      label: t('Roles'),
      options: roles,
      selected: selectedRoles,
      setSelected: setSelectedRoles,
    },
    {
      name: 'activities',
      label: t('Activities'),
      options: activities,
      selected: selectedActivities,
      setSelected: setSelectedActivities,
    },
    {
      name: 'filterGroups',
      label: t('Filter Groups'),
      options: customObjects,
      selected: selectedFilterGroups,
      setSelected: setSelectedFilterGroups,
    },
    {
      name: 'toolbarTemplates',
      label: t('Toolbar Templates'),
      options: toolbarTemplates,
      selected: selectedToolbarTemplates,
      setSelected: setSelectedToolbarTemplates,
    },
    {
      name: 'emailTemplates',
      label: t('Email Templates'),
      options: emailTemplates,
      selected: selectedEmailTemplates,
      setSelected: setSelectedEmailTemplates,
    },
    {
      name: 'automations',
      label: t('Agentic Workflows'),
      options: automations,
      selected: selectedAutomations,
      setSelected: setSelectedAutomations,
      type: 'automations',
    },
    {
      name: 'homepages',
      label: t('Homepages'),
      options: homepages,
      selected: selectedHomepages,
      setSelected: setSelectedHomepages,
    },
    {
      name: 'dashboards',
      label: t('Dashboards'),
      options: dashboards,
      selected: selectedDashboards,
      setSelected: setSelectedDashboards,
    },
    {
      name: 'chartGroups',
      label: t('Chart Groups'),
      options: customObjects,
      selected: selectedChartGroups,
      setSelected: setSelectedChartGroups,
    },
    {
      name: 'forms',
      label: t('Forms'),
      options: forms,
      selected: selectedForms,
      setSelected: setSelectedForms,
    },
    {
      name: 'surveys',
      label: t('Surveys'),
      options: surveys,
      selected: selectedSurveys,
      setSelected: setSelectedSurveys,
    },
    {
      name: 'smartConnectors',
      label: t('Smart Connectors'),
      options: smartConnectors,
      selected: selectedSmartConnectors,
      setSelected: setSelectedSmartConnectors,
    },
    {
      name: 'businessVariables',
      label: t('Business Variables'),
      options: businessVariables,
      selected: selectedBusinessVariables,
      setSelected: setSelectedBusinessVariables,
    },
    {
      name: 'teamMembersFields',
      label: t('Team Members Fields'),
      options: teamMembersFields,
      selected: selectedTeamMembersFields,
      setSelected: setSelectedTeamMembersFields,
    },
    {
      name: 'integrationSecrets',
      label: t('Integration Secrets'),
      options: integrationSecrets,
      selected: selectedIntegrationSecrets,
      setSelected: setSelectedIntegrationSecrets,
    },
  ];

  return (
    <>
      <SubSection
        title={t('Configuration Export')}
        action={
          <Button
            onClick={() => setShowFilenameModal(true)}
            disabled={!isDirty || submitting}
            data-qa="export-configuration-button"
          >
            {t('Export')}
          </Button>
        }
      >
        <KeyBoardContext.Provider value={{ assignFieldHandle }}>
          <Loader loading={loadingCustomObjects}>
            {checkboxesConfig.map(
              ({ name, label, options, selected, setSelected, type }) =>
                type === 'automations' ? (
                  <AutomationsExportGroup
                    key={name}
                    label={label}
                    allAutomations={options}
                    rootAutomations={rootAutomations}
                    folderTree={folderTree}
                    selectedAutomations={selected}
                    setSelectedAutomations={setSelected}
                    selectedFolders={selectedAutomationFolders}
                    setSelectedFolders={setSelectedAutomationFolders}
                  />
                ) : (
                  <ExportGroup
                    key={name}
                    keyListenersProps={getKeyListenersProps(name)}
                    label={label}
                    options={options}
                    selected={selected}
                    setSelected={setSelected}
                  />
                )
            )}
          </Loader>
        </KeyBoardContext.Provider>
      </SubSection>
      {showFilenameModal && (
        <ExportConfigurationModal
          onHide={() => setShowFilenameModal(false)}
          onConfirm={handleSubmit}
        />
      )}
    </>
  );
};
